import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import { ReduxStateConsumptionDetails } from "../../types/redux/reduxStateConsumptionDetails";
import { initialStateConsumptionDetails } from "../defaultState";
import { getAplError } from "../../aplApi/exceptionHelper";
import { IssuingRequest } from "../../types/issuingRequest";
import { issuePalletByIssuingRequest } from "../../aplApi/consumptionsHelper";
import {
    consumePallet,
    deleteConsumption,
    getActivityAndConsumptionDetailsByLineId,
    getActivityAndConsumptionDetailsByOperationId
} from "../../aplApi/consumptionHelper";
import { closeModalRedux } from "./modalPallets2.feature";
import { AplError } from "../../types/aplError";

export const getActivityAndConsumptionDetailsByOperationIdRedux = createAsyncThunk(
    "consumptionDetails/getActivityAndConsumptionDetailsByOperationIdRedux",
    async (operationId: string, thunkApi) => {
        try {
            return (await getActivityAndConsumptionDetailsByOperationId(operationId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const getActivityDetailsByOperationIdRedux = createAsyncThunk(
    "consumptionDetails/getActivityDetailsByOperationIdRedux",
    async (operationId: string, thunkApi) => {
        try {
            return (await getActivityAndConsumptionDetailsByOperationId(operationId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const getActivityAndConsumptionDetailsByLineIdRedux = createAsyncThunk(
    "consumptionDetails/getActivityAndConsumptionDetailsByLineIdRedux",
    async (lineId: number, thunkApi) => {
        try {
            let response = await getActivityAndConsumptionDetailsByLineId(lineId);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const issuePalletGetPalletDetails = createAsyncThunk(
    "consumptionDetails/issuePalletGetPalletDetails",
    async (issuingRequest: IssuingRequest, thunkApi) => {
        try {
            await issuePalletByIssuingRequest(issuingRequest);
            thunkApi.dispatch(closeModalRedux());
            return (await getActivityAndConsumptionDetailsByOperationId(issuingRequest.operationId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        } finally {
            // thunkApi.dispatch(closeModalRedux());
        }
    }
);

export const closeConsumptionGetPalletDetails = createAsyncThunk(
    "consumptionDetails/closeConsumptionGetPalletDetails",
    async (
        consumptionCloseRequest: {
            consumptionId: string;
            operationId: string;
        },
        thunkApi
    ) => {
        try {
            await consumePallet(consumptionCloseRequest.consumptionId, 0);
            thunkApi.dispatch(closeModalRedux());
            return (await getActivityAndConsumptionDetailsByOperationId(consumptionCloseRequest.operationId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        } finally {
            // thunkApi.dispatch(closeModalRedux());
        }
    }
);

export const deleteConsumptionGetPalletDetails = createAsyncThunk(
    "consumptionDetails/deleteConsumptionGetPalletDetails",
    async (
        consumptionCloseRequest: {
            consumptionId: string;
            operationId: string;
        },
        thunkApi
    ) => {
        try {
            await deleteConsumption(consumptionCloseRequest.consumptionId);
            return (await getActivityAndConsumptionDetailsByOperationId(consumptionCloseRequest.operationId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        } finally {
            thunkApi.dispatch(closeModalRedux());
        }
    }
);

const consumptionDetails: any = createSlice({
    name: "consumptionDetails",
    initialState: initialStateConsumptionDetails,
    reducers: {
        onClearConsumptionDetails: (): ReduxStateConsumptionDetails => initialStateConsumptionDetails,
        onSetOffline: function (state, action) {
            return { ...state, offline: action.payload };
        },
        onSetSelectedConsumptionDetails: function (state, action) {
            return { ...state, selectedConsumptionDetails: action.payload };
        },
        onDtStartChange: (state, action): ReduxStateConsumptionDetails => ({
            ...state,
            dateStartSelectorDate: { ...moment(action.payload).toObject() }
        }),
        onDtEndChange: (state, action): ReduxStateConsumptionDetails => ({
            ...state,
            dateEndSelectorDate: { ...moment(action.payload).toObject() }
        }),

        onManualBarcodeInputChange: (state, action): ReduxStateConsumptionDetails => ({ ...state, manualBarcodeInput: action.payload }),
        onSetConsumptionOperationError: (state, action): ReduxStateConsumptionDetails => ({
            ...state,
            consumptionOperationError: action.payload
        })
    },
    extraReducers: builder => {
        builder
            .addCase(
                getActivityAndConsumptionDetailsByOperationIdRedux.pending,
                (state): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: true,
                    consumptionDetailsLoading: true
                })
            )
            .addCase(
                getActivityAndConsumptionDetailsByOperationIdRedux.fulfilled,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: false,
                    consumptionDetailsLoading: false,
                    consumptionDetailsFirstLoaded: true,
                    consumptionDetailsError: null,
                    consumptionDetails: action.payload.consumptionDetails,
                    currentActivity: [action.payload.activity]
                })
            )
            .addCase(
                getActivityAndConsumptionDetailsByOperationIdRedux.rejected,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...initialStateConsumptionDetails,
                    consumptionDetailsLoading: false,
                    consumptionDetails: [],
                    consumptionDetailsError: action.error.code === "ERR_CANCELED" ? null : (action.payload as AplError)
                })
            );

        builder
            .addCase(
                getActivityDetailsByOperationIdRedux.pending,
                (state): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: true
                })
            )
            .addCase(
                getActivityDetailsByOperationIdRedux.fulfilled,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: false,
                    currentActivityError: null,
                    currentActivity: action.payload
                })
            )
            .addCase(
                getActivityDetailsByOperationIdRedux.rejected,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: false,
                    currentActivityError: action.error.code === "ERR_CANCELED" ? null : (action.payload as AplError),
                    currentActivity: []
                })
            );

        builder
            .addCase(
                getActivityAndConsumptionDetailsByLineIdRedux.pending,
                (state): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: true,
                    consumptionDetailsLoading: true
                })
            )
            .addCase(
                getActivityAndConsumptionDetailsByLineIdRedux.fulfilled,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: false,
                    consumptionDetailsLoading: false,
                    currentActivityError: null,
                    consumptionDetailsFirstLoaded: true,
                    consumptionDetails: action.payload.consumptionDetails,
                    currentActivity: [action.payload.activity]
                })
            )
            .addCase(
                getActivityAndConsumptionDetailsByLineIdRedux.rejected,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    currentActivityLoading: false,
                    consumptionDetailsLoading: false,
                    currentActivityError: action.error.code === "ERR_CANCELED" ? null : (action.payload as AplError),
                    currentActivity: []
                })
            );

        builder
            .addCase(
                issuePalletGetPalletDetails.pending,
                (state): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: true
                })
            )
            .addCase(
                issuePalletGetPalletDetails.fulfilled,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: false,
                    currentActivityLoading: false,
                    consumptionOperationError: null,
                    consumptionDetails: action.payload.consumptionDetails,
                    currentActivity: [action.payload.activity],
                    selectedConsumptionDetails: action.payload.consumptionDetails.length > 0 ? [action.payload.consumptionDetails[0]] : []
                })
            )
            .addCase(
                issuePalletGetPalletDetails.rejected,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: false,
                    currentActivityLoading: false,
                    consumptionOperationError: action.error.code === "ERR_CANCELED" ? null : (action.payload as AplError)
                })
            );

        builder
            .addCase(
                closeConsumptionGetPalletDetails.pending,
                (state): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: true
                })
            )
            .addCase(
                closeConsumptionGetPalletDetails.fulfilled,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: false,
                    currentActivityLoading: false,
                    consumptionOperationError: null,
                    consumptionDetails: action.payload.consumptionDetails,
                    currentActivity: [action.payload.activity]
                })
            )
            .addCase(
                closeConsumptionGetPalletDetails.rejected,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: false,
                    currentActivityLoading: false,
                    consumptionOperationError: action.error.code === "ERR_CANCELED" ? null : (action.payload as AplError)
                })
            );

        builder
            .addCase(
                deleteConsumptionGetPalletDetails.pending,
                (state): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: true
                })
            )
            .addCase(
                deleteConsumptionGetPalletDetails.fulfilled,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: false,
                    currentActivityLoading: false,
                    consumptionOperationError: null,
                    consumptionDetails: action.payload.consumptionDetails,
                    currentActivity: [action.payload.activity]
                })
            )
            .addCase(
                deleteConsumptionGetPalletDetails.rejected,
                (state, action): ReduxStateConsumptionDetails => ({
                    ...state,
                    consumptionDetailsLoading: false,
                    consumptionOperationError: action.error.code === "ERR_CANCELED" ? null : (action.payload as AplError)
                })
            );
    }
});

export const {
    onClearConsumptionDetails,
    onSetOffline,
    onSetSelectedConsumptionDetails,
    onDtStartChange,
    onDtEndChange,
    onManualBarcodeInputChange,
    onSetConsumptionOperationError
} = consumptionDetails.actions;
export default consumptionDetails.reducer;
